import type { CSSProperties, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { THEMED_SURFACE_CLASS } from "../hooks/useThemedSurface";

interface BookingFormLauncherProps {
  /** Focus returns here when the full-screen booking form closes. */
  readonly buttonRef: RefObject<HTMLButtonElement | null>;
  /** The design marker's attributes, from the widget's `useThemedSurface`. */
  readonly themeAttributes: Record<string, string>;
  readonly style?: CSSProperties;
  readonly onOpen: () => void;
}

/**
 * The bar the widget shows instead of itself on a narrow viewport (see the
 * `show-book-now-smaller-than` attribute): a single control, fixed to the
 * bottom of the embedding page, that opens the booking journey full-screen.
 *
 * It renders beside the widget root rather than inside it, because the root is
 * a query container (`gcw:@container`) and so would be the containing block for
 * a `position: fixed` descendant, pinning the bar to the widget instead of to
 * the browser window. Being its own root, it carries the design marker itself
 * so it resolves the tokens it paints with.
 */
export function BookingFormLauncher({ buttonRef, themeAttributes, style, onOpen }: Readonly<BookingFormLauncherProps>) {
  const { t } = useTranslation();

  return (
    <div className={`booking-form-launcher ${THEMED_SURFACE_CLASS}`} style={style} {...themeAttributes}>
      <button ref={buttonRef} type="button" className="booking-form-launcher__button" onClick={onOpen}>
        {t("booking_form_book_now", "Book now")}
      </button>
    </div>
  );
}
